import React, { memo, useRef, useEffect } from 'react';
import { Frame, RosterEntry, WEAPON_NAMES } from './replayParser';
import { getWeaponDataUri, getUtilDataUri, getEquipmentDataUri, getArmorDataUri, getIndicatorDataUri } from './weaponIcons';
import styles from './ReplayScoreboard.module.css';

interface ReplayScoreboardProps {
    frame: Frame;
    roster: RosterEntry[];
    playerNames: Record<string, string>;
    side: 'T' | 'CT';
    position?: 'left' | 'right';
    teamName: string;
    layout?: 'split' | 'stacked' | 'mobile';
    killCounts?: Record<number, number>;
    /** Last non-knife weapon per player slot up to the current frame. Used to display the
     *  holstered main weapon in the side panel when the player currently has a knife out. */
    mainWeaponBySlot?: number[];
    /** This side's buy for the round, from the match's round ends (same source as /analyse). */
    buyType?: string | null;
    equipValue?: number | null;
    /** HP each player has taken off enemies so far this round. */
    damageBySlot?: Record<number, number>;
    /** HP lost in the last second, with 1 -> 0 as the hit ages, for the red flash on the bar.
     *  No number: next to "59 dmg" (dealt) a "-96" (taken) read as the same thing. */
    recentHitBySlot?: Record<number, { damage: number; fade: number }>;
}

const BUY_LABELS: Record<string, string> = {
    pistol: 'Pistol', eco: 'Eco', 'semi-eco': 'Semi Eco', 'semi-buy': 'Semi Buy', 'full-buy': 'Full Buy',
};

const T_COLOR = '#de9b35';
const CT_COLOR = '#5d79ae';

function getArmorIcon(armor: number, flags: number): string {
    if (armor === 0) return getArmorDataUri('none');
    const hasHelmet = (flags & (1 << 4)) !== 0;
    return hasHelmet ? getArmorDataUri('helmet') : getArmorDataUri('vest');
}

/** Stable <img> that updates src via ref — no DOM remount, no network refetch */
const StableImg: React.FC<{ src: string; alt?: string; className?: string; style?: React.CSSProperties }> = memo(({ src, alt, className, style }) => {
    const ref = useRef<HTMLImageElement>(null);
    const prevSrc = useRef(src);

    useEffect(() => {
        if (ref.current && src !== prevSrc.current) {
            ref.current.src = src;
            prevSrc.current = src;
        }
    }, [src]);

    return <img ref={ref} src={src} alt={alt || ''} className={className} style={style} />;
}, (prev, next) => prev.src === next.src && prev.className === next.className);

const ReplayScoreboard: React.FC<ReplayScoreboardProps> = ({
    frame, roster, playerNames, side, position = 'left', teamName, layout = 'split', killCounts = {},
    mainWeaponBySlot, buyType, equipValue, damageBySlot = {}, recentHitBySlot = {},
}) => {
    const color = side === 'T' ? T_COLOR : CT_COLOR;
    const isCT = side === 'CT';
    const isStacked = layout === 'stacked';

    const teamPlayers = roster
        .map((entry, idx) => ({ entry, idx }))
        .filter(({ entry }) => entry.team === side);

    const aliveCount = teamPlayers.filter(({ idx }) => frame.players[idx]?.isAlive).length;

    // Mobile layout: compact rows — name + money + weapon only
    if (layout === 'mobile') {
        return (
            <div className={styles.mobileScoreboard}>
                {teamPlayers.map(({ entry, idx }) => {
                    const player = frame.players[idx];
                    if (!player) return null;
                    const name = playerNames[entry.steamId] || `Player ${idx + 1}`;
                    const alive = player.isAlive;
                    const hpPercent = alive ? Math.max(player.hp, 0) : 0;
                    // Prefer holstered main weapon when knife is currently out.
                    const holstered = mainWeaponBySlot?.[idx];
                    const displayWeaponId = player.weaponId === 60 && holstered ? holstered : player.weaponId;
                    const weaponSrc = getWeaponDataUri(displayWeaponId);

                    return (
                        <div
                            key={entry.steamId}
                            className={`${styles.mobileRow} ${!alive ? styles.mobileRowDead : ''}`}
                        >
                            <div
                                className={`${styles.mobileHpFill} ${isCT ? styles.mobileHpFillCT : styles.mobileHpFillT}`}
                                style={{ width: `${hpPercent}%` }}
                            />
                            <span className={styles.mobileName}>{name}</span>
                            <span className={styles.mobileMoney}>${player.money}</span>
                            {weaponSrc && alive && (
                                <img className={styles.mobileWeapon} src={weaponSrc} alt="" />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // Scoreboard classes
    const scoreboardClasses = [
        styles.scoreboard,
        isCT ? styles.scoreboardCT : styles.scoreboardT,
        isStacked ? styles.scoreboardStacked : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={scoreboardClasses}>
            {/* Team header */}
            <div
                className={styles.teamHeader}
                style={{ borderBottom: `2px solid ${color}` }}
            >
                <span className={styles.teamName}>{teamName}</span>
                {(buyType || equipValue) && (
                    <span className={styles.buyInfo} title="Buy type and equipment value at the start of the round">
                        {buyType && <span>{BUY_LABELS[buyType] ?? buyType}</span>}
                        {equipValue ? <span className={styles.buyValue}>${equipValue.toLocaleString('en-US')}</span> : null}
                    </span>
                )}
                <span className={styles.aliveBadge} style={{ backgroundColor: color }}>
                    {aliveCount}
                </span>
            </div>

            {/* Player rows */}
            {teamPlayers.map(({ entry, idx }) => {
                const player = frame.players[idx];
                if (!player) return null;

                const name = playerNames[entry.steamId] || `Player ${idx + 1}`;
                // Prefer holstered main weapon when knife is currently out — keeps the side panel
                // showing what the player can actually fight with. The map overlay still shows knife.
                const holstered = mainWeaponBySlot?.[idx];
                const displayWeaponId = player.weaponId === 60 && holstered ? holstered : player.weaponId;
                const isShowingHolstered = displayWeaponId !== player.weaponId;
                const weapon = WEAPON_NAMES[displayWeaponId] || '';
                const alive = player.isAlive;
                const hp = alive ? Math.max(player.hp, 0) : 0;
                const hpPercent = `${hp}%`;
                const hit = recentHitBySlot[idx];
                const damage = damageBySlot[idx] || 0;
                const mirrored = isCT && !isStacked;

                // Name bar classes
                const nameBarClasses = [
                    styles.nameBar,
                    isCT && !isStacked ? styles.nameBarCT : '',
                    isStacked ? styles.nameBarStacked : '',
                    !alive ? styles.nameBarDead : '',
                ].filter(Boolean).join(' ');

                const fillClasses = [
                    styles.nameBarFill,
                    alive ? (isCT ? styles.nameBarFillCT : styles.nameBarFillT) : '',
                ].filter(Boolean).join(' ');

                // Equipment row classes
                const equipClasses = [
                    styles.equipRow,
                    isCT && !isStacked ? styles.equipRowCT : '',
                    isStacked ? styles.equipRowStacked : '',
                ].filter(Boolean).join(' ');

                // Utility section classes
                const utilClasses = [
                    styles.utilSection,
                    isCT && !isStacked ? styles.utilSectionCT : '',
                    isStacked ? styles.utilSectionStacked : '',
                ].filter(Boolean).join(' ');

                return (
                    <div
                        key={entry.steamId}
                        className={`${styles.playerRow} ${!alive ? styles.playerRowDead : ''}`}
                    >
                        {/* CS2Lens-style name bar: team-colored, width = HP */}
                        <div className={nameBarClasses}>
                            <div
                                className={fillClasses}
                                style={{ width: hpPercent }}
                            />
                            {/* The HP just lost, sitting where the fill used to reach, fading out */}
                            {hit && (
                                <div
                                    className={styles.hitFill}
                                    style={{
                                        [mirrored ? 'right' : 'left']: hpPercent,
                                        width: `${Math.min(hit.damage, 100 - hp)}%`,
                                        opacity: hit.fade,
                                    }}
                                />
                            )}
                            <div className={styles.nameBarContent}>
                                <span className={`${styles.playerName} ${!alive ? styles.playerNameDead : ''}`}>
                                    {name}
                                </span>
                                {damage > 0 && (
                                    <span className={styles.damageDealt} title="Damage dealt this round">
                                        {damage} dmg
                                    </span>
                                )}
                                {(killCounts[idx] || 0) > 0 && (
                                    <span className={styles.killCount}>
                                        <img src={getIndicatorDataUri('skull')} alt="" className={styles.killIcon} />
                                        {killCounts[idx]}
                                    </span>
                                )}
                                <span className={styles.playerMoney}>
                                    ${player.money}
                                </span>
                            </div>
                        </div>

                        {/* Equipment row: always in DOM, hidden when dead to avoid img remounts */}
                        <div className={equipClasses} style={alive ? undefined : { display: 'none' }}>
                            <StableImg
                                className={`${styles.armorIcon} ${player.armor === 0 ? styles.armorNone : ''}`}
                                src={getArmorIcon(player.armor, player.flags)}
                                alt="armor"
                            />
                            {getWeaponDataUri(displayWeaponId) ? (
                                <StableImg
                                    className={styles.weaponIcon}
                                    src={getWeaponDataUri(displayWeaponId)!}
                                    alt={weapon}
                                />
                            ) : (
                                <span className={styles.weaponText}>{weapon}</span>
                            )}
                            {!isShowingHolstered && player.weaponId >= 1 && player.weaponId <= 51 && (
                                <span className={styles.ammoText}>
                                    {player.ammoClip}/{player.ammoReserve}
                                </span>
                            )}
                            <div className={utilClasses}>
                                {(player.flags & (1 << 5)) !== 0 && (
                                    <StableImg className={styles.utilIcon} src={getWeaponDataUri(80)!} alt="C4" />
                                )}
                                {(player.flags & (1 << 6)) !== 0 && (
                                    <StableImg className={styles.utilIcon} src={getEquipmentDataUri('defuseKit')!} alt="Kit" />
                                )}
                                {player.utilityFlash > 0 && <UtilIcon count={player.utilityFlash} src={getUtilDataUri('flash')} />}
                                {player.utilitySmoke > 0 && <UtilIcon count={player.utilitySmoke} src={getUtilDataUri('smoke')} />}
                                {player.utilityHE > 0 && <UtilIcon count={player.utilityHE} src={getUtilDataUri('he')} />}
                                {player.utilityFire > 0 && <UtilIcon count={player.utilityFire} src={getUtilDataUri('fire')} />}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const UtilIcon: React.FC<{ count: number; src: string }> = memo(({ count, src }) => (
    <div className={styles.utilGroup}>
        {Array.from({ length: count }, (_, i) => (
            <StableImg key={i} src={src} alt="" className={styles.utilIcon} />
        ))}
    </div>
));

export default memo(ReplayScoreboard);
